import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Form, Input, Modal, Radio, Select } from 'antd';
import { ReadOutlined } from '@ant-design/icons';
import { useCompetencyAction, useCompetencyCan, COMPETENCY_PERMS } from '@/hooks/useCompetency';
import { competencyService } from '@/services/competency.service';
import departmentService from '@/services/department.service';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  sourceType: 'NCR' | 'CAPA' | 'MOC';
  sourceId: string;
  /** Pre-filled note, e.g. the root cause or change summary. */
  defaultNotes?: string;
  size?: 'small' | 'middle';
}

const REQUEST_PERMS = [COMPETENCY_PERMS.trainingManage, COMPETENCY_PERMS.view, 'quality.ncr.create', 'quality.capa.create', 'moc.create', 'moc.edit'];

/** Adds training lines to the annual training schedule from an NCR, CAPA or MOC. */
const RequestTrainingButton: React.FC<Props> = ({ sourceType, sourceId, defaultNotes, size }) => {
  const allowed = useCompetencyCan(...REQUEST_PERMS);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const target = Form.useWatch('target', form);
  const { data } = useQuery({
    queryKey: ['competency', 'request-lookups'],
    queryFn: () => competencyService.requestTrainingLookups(),
    enabled: open,
    staleTime: 300_000,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection'],
    queryFn: () => departmentService.getForSelection(),
    enabled: open,
    staleTime: 300_000,
  });
  const submit = useCompetencyAction(
    (v: { course_id: string; target: string; employee_ids?: string[]; department_id?: string; planned_months?: number[]; notes?: string }) =>
      competencyService.requestTraining({
        source_type: sourceType,
        source_id: sourceId,
        course_id: v.course_id,
        employee_ids: v.target === 'employees' ? v.employee_ids : [],
        department_id: v.target === 'department' ? v.department_id : null,
        planned_months: v.planned_months,
        notes: v.notes || null,
      }),
    'Training added to the annual schedule'
  );

  if (!allowed) return null;

  return (
    <>
      <Button size={size} icon={<ReadOutlined />} onClick={() => setOpen(true)}>
        Request training
      </Button>
      <Modal
        title={`Request training from ${sourceType}`}
        open={open}
        onCancel={() => setOpen(false)}
        okText="Add to schedule"
        confirmLoading={submit.isPending}
        destroyOnClose
        onOk={async () => {
          const v = await form.validateFields();
          await submit.mutateAsync(v);
          setOpen(false);
          form.resetFields();
        }}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{ target: 'employees', notes: defaultNotes, planned_months: [new Date().getMonth() + 1] }}
        >
          <Form.Item name="course_id" label="Training course" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={(data?.courses ?? []).map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))} />
          </Form.Item>
          <Form.Item name="target" label="Who needs the training">
            <Radio.Group options={[{ value: 'employees', label: 'Employees' }, { value: 'department', label: 'Whole department' }]} optionType="button" />
          </Form.Item>
          {target === 'department' ? (
            <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.department_name }))} />
            </Form.Item>
          ) : (
            <Form.Item name="employee_ids" label="Employees" rules={[{ required: true }]}>
              <Select
                mode="multiple"
                optionFilterProp="label"
                options={(data?.employees ?? []).map((e) => ({
                  value: e.id,
                  label: `${e.employee_number} - ${e.full_name}${e.department_name ? ` (${e.department_name})` : ''}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item name="planned_months" label="Planned month(s)" rules={[{ required: true }]}>
            <Checkbox.Group options={MONTH_LABELS.map((m, i) => ({ value: i + 1, label: m }))} />
          </Form.Item>
          <Form.Item name="notes" label="Reason">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RequestTrainingButton;
